import { useEffect, useRef, useState } from 'react'
import { Check, Globe } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/utils/cn'
import { LOCALES, LOCALE_LABELS, isLocale, type Locale } from '@/i18n'

/** Language picker. EN lives at the root; other locales are URL-prefixed
 *  (/fr, /th, …). Switching does a full navigation to the target locale's URL
 *  (the router basename is fixed per load), and persists the choice in a cookie. */
export default function LanguageSwitcher({
  className = '',
  variant = 'button',
}: {
  className?: string
  /** `button` = bordered dropdown pill (header). `bare` = borderless dropdown
   *  trigger, to sit inside a shared container. `menu` = inline grid of all six
   *  locales. `compact` = tiny inline row of language codes (EN · FR · …). */
  variant?: 'button' | 'bare' | 'menu' | 'compact'
}) {
  const { i18n } = useTranslation()
  const current: Locale = isLocale(i18n.language) ? i18n.language : 'en'
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const switchTo = (target: Locale) => {
    const path = window.location.pathname
    const seg = path.split('/')[1]
    const stripped =
      isLocale(seg) && seg !== 'en' ? path.slice(seg.length + 1) || '/' : path
    const next =
      target === 'en'
        ? stripped
        : `/${target}${stripped === '/' ? '' : stripped}`
    document.cookie = `tla_lang=${target}; path=/; max-age=31536000; samesite=lax`
    window.location.assign(next + window.location.search)
  }

  // Tiny inline row of language codes — current one highlighted, the rest dimmed.
  if (variant === 'compact') {
    return (
      <div className={cn('flex items-center gap-0.5', className)}>
        {LOCALES.map((l) => (
          <button
            key={l}
            type="button"
            onClick={() => switchTo(l)}
            aria-current={l === current}
            aria-label={LOCALE_LABELS[l]}
            className={cn(
              'rounded px-1.5 py-1 text-xs font-semibold uppercase tracking-wide transition-colors',
              l === current
                ? 'text-garage-accent'
                : 'text-garage-cream/40 hover:text-garage-cream/80'
            )}
          >
            {l}
          </button>
        ))}
      </div>
    )
  }

  // Inline list for the mobile folding menu — all six visible, no dropdown.
  if (variant === 'menu') {
    return (
      <div className={cn('grid grid-cols-2 gap-2', className)}>
        {LOCALES.map((l) => (
          <button
            key={l}
            type="button"
            onClick={() => switchTo(l)}
            aria-current={l === current}
            className={cn(
              'flex items-center justify-between gap-2 rounded-lg border px-3 py-2.5 text-sm transition-colors',
              l === current
                ? 'border-garage-accent/40 bg-garage-accent/15 text-garage-accent'
                : 'border-garage-accent/20 bg-garage-card/40 text-garage-cream/80 hover:text-garage-cream'
            )}
          >
            {LOCALE_LABELS[l]}
            {l === current && <Check className="h-4 w-4 shrink-0" />}
          </button>
        ))}
      </div>
    )
  }

  return (
    <div ref={ref} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={LOCALE_LABELS[current]}
        className={cn(
          'flex items-center gap-2 text-garage-cream transition-all duration-300',
          variant === 'bare'
            ? 'rounded-md p-2 hover:bg-garage-card/60'
            : 'rounded-lg border border-garage-accent/20 bg-garage-card/40 p-3 backdrop-blur-sm hover:border-garage-accent/40 hover:bg-garage-card/60'
        )}
      >
        <Globe className="h-5 w-5" />
        <span className="text-sm font-semibold uppercase">{current}</span>
      </button>
      {open && (
        <ul
          role="listbox"
          className="absolute right-0 z-50 mt-2 min-w-[11rem] overflow-hidden rounded-lg border border-garage-accent/20 bg-garage-dark/95 shadow-garage-xl backdrop-blur-md"
        >
          {LOCALES.map((l) => (
            <li key={l}>
              <button
                type="button"
                role="option"
                aria-selected={l === current}
                onClick={() => switchTo(l)}
                className={cn(
                  'flex w-full items-center justify-between gap-3 px-4 py-2.5 text-sm transition-colors',
                  l === current
                    ? 'bg-garage-accent/10 text-garage-accent'
                    : 'text-garage-cream/80 hover:bg-garage-card/40 hover:text-garage-cream'
                )}
              >
                {LOCALE_LABELS[l]}
                {l === current && <Check className="h-4 w-4" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
